/**
 * Card ID Normalization Utilities
 * 
 * Handles conversion between different card ID formats introduced around Set 6.
 * Ensures consistent matching between collection items and marketplace listings.
 * 
 * Format examples:
 * - Old format: "123"
 * - New format: "123_normal" or "123_foil"
 */

/**
 * Normalize a card ID by removing type suffixes
 * @param {string} cardId - Full card ID (may include _normal or _foil suffix)
 * @returns {string} Base card ID without suffix
 */
export const normalizeCardId = (cardId) => {
  if (!cardId || typeof cardId !== 'string') {
    return cardId;
  }
  return cardId.replace(/_(normal|foil)$/i, '');
};

/**
 * Construct full card ID with appropriate suffix
 * @param {string} baseId - Base card ID without suffix
 * @param {string} type - Card type: "Normal" or "Foil"
 * @returns {string} Full card ID with suffix
 */
export const getFullCardId = (baseId, type) => {
  if (!baseId || typeof baseId !== 'string') {
    return baseId;
  }
  
  // If already has suffix, return as-is
  if (/_(?:normal|foil)$/i.test(baseId)) {
    return baseId;
  }
  
  // Add suffix based on type
  const normalizedType = (type || 'normal').toLowerCase();
  if (normalizedType === 'foil') {
    return `${baseId}_foil`;
  }
  return `${baseId}_normal`;
};

/**
 * Compare two card IDs accounting for different formats
 * @param {string} id1 - First card ID
 * @param {string} id2 - Second card ID
 * @returns {boolean} True if IDs match after normalization
 */
export const matchCardIdVariants = (id1, id2) => {
  if (!id1 || !id2) {
    return false;
  }
  
  // Direct match
  if (id1 === id2) {
    return true;
  }
  
  // Normalized match
  const normalized1 = normalizeCardId(id1);
  const normalized2 = normalizeCardId(id2);
  
  return normalized1 === normalized2;
};

/**
 * Extract card type from card ID
 * @param {string} cardId - Full card ID
 * @returns {string} "Normal" or "Foil"
 */
export const getCardTypeFromId = (cardId) => {
  if (!cardId || typeof cardId !== 'string') {
    return 'Normal';
  }
  
  if (/_foil$/i.test(cardId)) {
    return 'Foil';
  }
  
  return 'Normal';
};

/**
 * Generate all possible ID variants for a card
 * Used for fallback queries when exact match fails
 * @param {string} cardId - Base or full card ID
 * @returns {string[]} Array of possible card ID variants
 */
export const getCardIdVariants = (cardId) => {
  if (!cardId) {
    return [];
  }
  
  const normalized = normalizeCardId(cardId);
  const variants = [
    cardId,                    // Original
    normalized,                 // Base without suffix
    `${normalized}_normal`,     // With normal suffix
    `${normalized}_foil`,       // With foil suffix
  ];
  
  // Remove duplicates
  return [...new Set(variants)];
};

/**
 * Check if a card ID has a type suffix
 * @param {string} cardId - Card ID to check
 * @returns {boolean} True if has _normal or _foil suffix
 */
export const hasTypeSuffix = (cardId) => {
  if (!cardId || typeof cardId !== 'string') {
    return false;
  }
  return /_(?:normal|foil)$/i.test(cardId);
};
